import { useState, useCallback } from "react";
import {
  ShieldCheck,
  Check,
  X,
  Plus,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Inbox,
  Loader2,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { privacyApi } from "@/lib/admin-api";
import type { DsarRequest } from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: readonly {
  readonly value: string;
  readonly label: string;
}[] = [
  { value: "", label: "Tous les statuts" },
  { value: "received", label: "Recues" },
  { value: "in_progress", label: "En cours" },
  { value: "completed", label: "Terminees" },
  { value: "rejected", label: "Rejetees" },
];

const TYPE_OPTIONS: readonly {
  readonly value: string;
  readonly label: string;
}[] = [
  { value: "", label: "Tous les types" },
  { value: "access", label: "Acces" },
  { value: "rectification", label: "Rectification" },
  { value: "deletion", label: "Suppression" },
  { value: "opposition", label: "Opposition" },
  { value: "portability", label: "Portabilite" },
  { value: "export", label: "Export de donnees" },
];

const STATUS_LABELS: Record<string, string> = {
  received: "Recue",
  in_progress: "En cours",
  completed: "Terminee",
  rejected: "Rejetee",
};

const STATUS_COLORS: Record<string, string> = {
  received: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const TYPE_LABELS: Record<string, string> = {
  access: "Acces",
  rectification: "Rectification",
  deletion: "Suppression",
  opposition: "Opposition",
  portability: "Portabilite",
  export: "Export de donnees",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN");
}

function isOverdue(status: string, deadline: string): boolean {
  return (
    status !== "completed" &&
    status !== "rejected" &&
    new Date(deadline) < new Date()
  );
}

function getDaysRemaining(deadline: string): number {
  const now = new Date();
  const dl = new Date(deadline);
  return Math.ceil((dl.getTime() - now.getTime()) / 86_400_000);
}

function getIdentifier(req: DsarRequest): string {
  return req.identifier_email ?? req.identifier_phone ?? "\u2014";
}

// ---------------------------------------------------------------------------
// Countdown badge
// ---------------------------------------------------------------------------

function DeadlineBadge({
  status,
  deadline,
}: {
  readonly status: string;
  readonly deadline: string;
}) {
  if (status === "completed" || status === "rejected") {
    return (
      <span className="text-xs text-stone-400">{formatDate(deadline)}</span>
    );
  }

  const days = getDaysRemaining(deadline);
  const overdue = days < 0;

  if (overdue) {
    return (
      <span className="inline-flex animate-pulse items-center gap-1 rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
        <AlertTriangle className="h-3 w-3" />
        En retard ({Math.abs(days)}j)
      </span>
    );
  }

  if (days <= 7) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
        <Clock className="h-3 w-3" />
        {days}j restants
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs text-stone-500">
      <Clock className="h-3 w-3" />
      {days}j restants
    </span>
  );
}

// ---------------------------------------------------------------------------
// New DSAR request modal form
// ---------------------------------------------------------------------------

interface DsarFormData {
  type: string;
  identifier_email: string;
  identifier_phone: string;
  details: string;
}

function NewDsarModal({
  open,
  onClose,
  onSubmit,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (data: DsarFormData) => void;
}) {
  const [form, setForm] = useState<DsarFormData>({
    type: "access",
    identifier_email: "",
    identifier_phone: "",
    details: "",
  });

  const handleChange = useCallback(
    (field: keyof DsarFormData, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.identifier_email && !form.identifier_phone) {
        toast.error("Veuillez fournir un email ou un numero de telephone.");
        return;
      }
      if (!form.details.trim()) {
        toast.error("Veuillez decrire la demande.");
        return;
      }
      onSubmit(form);
      setForm({ type: "access", identifier_email: "", identifier_phone: "", details: "" });
    },
    [form, onSubmit],
  );

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Nouvelle demande DSAR"
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="dsar-form"
            className="rounded-md bg-[#C75B39] px-4 py-2 text-sm font-medium text-white hover:bg-[#B04E30]"
          >
            Soumettre la demande
          </button>
        </>
      }
    >
      <form id="dsar-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Type */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Type de demande
          </label>
          <select
            value={form.type}
            onChange={(e) => handleChange("type", e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            {TYPE_OPTIONS.filter((o) => o.value).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Email */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Email du demandeur
          </label>
          <input
            type="email"
            value={form.identifier_email}
            onChange={(e) => handleChange("identifier_email", e.target.value)}
            placeholder="exemple@email.sn"
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Phone */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Telephone du demandeur
          </label>
          <input
            type="tel"
            value={form.identifier_phone}
            onChange={(e) => handleChange("identifier_phone", e.target.value)}
            placeholder="+221 77 123 45 67"
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
          <p className="mt-1 text-xs text-stone-400">
            Au moins un identifiant (email ou telephone) est requis.
          </p>
        </div>

        {/* Details */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Description de la demande
          </label>
          <textarea
            value={form.details}
            onChange={(e) => handleChange("details", e.target.value)}
            rows={3}
            placeholder="Decrivez la demande du client..."
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <p className="text-xs text-blue-700">
            Conformement a la Loi 2008-12, les demandes DSAR doivent etre traitees dans un delai de 30 jours
            a compter de la date de reception.
          </p>
        </div>
      </form>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Privacy() {
  useAdminPageTitle("Vie privee");

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const queryClient = useQueryClient();

  const params: Record<string, string> = {
    page: String(page),
    limit: "20",
  };
  if (statusFilter) params.status = statusFilter;
  if (typeFilter) params.type = typeFilter;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["privacy-requests", params],
    queryFn: () => privacyApi.listRequests(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const executeMutation = useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      readonly id: string;
      readonly action: "approve" | "reject";
    }) => privacyApi.executeRequest(id, action),
    onMutate: ({ id }) => {
      setPendingId(id);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["privacy-requests"] });
      toast.success(
        variables.action === "approve"
          ? "Demande approuvee avec succes."
          : "Demande rejetee.",
      );
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : "Une erreur est survenue.",
      );
    },
    onSettled: () => {
      setPendingId(null);
    },
  });

  const apiRequests = data?.requests ?? [];

  const displayRequests = apiRequests;

  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0 };

  // KPIs - computed from API data
  const receivedCount = apiRequests.filter((r) => r.status === "received").length;
  const inProgressCount = apiRequests.filter((r) => r.status === "in_progress").length;
  const completedCount = apiRequests.filter((r) => r.status === "completed").length;
  const overdueCount = apiRequests.filter((r) =>
    isOverdue(r.status, r.sla_deadline),
  ).length;

  const handleApprove = useCallback(
    (id: string) => {
      executeMutation.mutate({ id, action: "approve" });
    },
    [executeMutation],
  );

  const handleReject = useCallback(
    (id: string) => {
      executeMutation.mutate({ id, action: "reject" });
    },
    [executeMutation],
  );

  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handleTypeChange = useCallback((value: string) => {
    setTypeFilter(value);
    setPage(1);
  }, []);

  const createMutation = useMutation({
    mutationFn: (formData: DsarFormData) =>
      privacyApi.createRequest({
        type: formData.type,
        identifier_email: formData.identifier_email || undefined,
        identifier_phone: formData.identifier_phone || undefined,
        details: formData.details || undefined,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["privacy-requests"] });
      toast.success(
        `Demande DSAR de type "${TYPE_LABELS[variables.type] ?? variables.type}" creee avec succes.`,
      );
      setShowNewModal(false);
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : "Une erreur est survenue lors de la creation.",
      );
    },
  });

  const handleNewDsar = useCallback(
    (formData: DsarFormData) => {
      createMutation.mutate(formData);
    },
    [createMutation],
  );

  const columns: readonly ColumnDef<DsarRequest>[] = [
    {
      id: "id",
      header: "ID",
      cell: (row) => (
        <span className="font-mono text-xs font-medium text-stone-600">
          {row.id}
        </span>
      ),
    },
    {
      id: "identifier",
      header: "Demandeur",
      cell: (row) => (
        <div>
          <span className="text-sm text-stone-800">
            {getIdentifier(row)}
          </span>
        </div>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (row) => (
        <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2.5 py-0.5 text-xs font-medium text-stone-700">
          {TYPE_LABELS[row.type] ?? row.type}
        </span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => {
        const overdue = isOverdue(row.status, row.sla_deadline);
        return (
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
              overdue
                ? "animate-pulse border-red-300 bg-red-100 text-red-700"
                : STATUS_COLORS[row.status] ?? "bg-stone-100 text-stone-600 border-stone-200"
            }`}
          >
            {overdue ? "En retard" : (STATUS_LABELS[row.status] ?? row.status)}
          </span>
        );
      },
    },
    {
      id: "created_at",
      header: "Date soumise",
      cell: (row) => (
        <span className="text-xs text-stone-500">
          {formatDate(row.created_at)}
        </span>
      ),
    },
    {
      id: "sla_deadline",
      header: "Date limite",
      cell: (row) => (
        <DeadlineBadge status={row.status} deadline={row.sla_deadline} />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) => {
        const isPending =
          row.status === "received" || row.status === "in_progress";
        if (!isPending) return null;
        const isProcessing = pendingId === row.id;
        return (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => handleApprove(row.id)}
              disabled={isProcessing}
              className="flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
              title="Approuver"
            >
              {isProcessing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              Approuver
            </button>
            <button
              onClick={() => handleReject(row.id)}
              disabled={isProcessing}
              className="flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
              title="Rejeter"
            >
              {isProcessing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <X className="h-3.5 w-3.5" />
              )}
              Rejeter
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Vie privee
              </h1>
              <p className="text-sm text-stone-500">
                Gestion des demandes de protection des donnees (DSAR) — Loi
                2008-12
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center gap-2 rounded-md bg-[#C75B39] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#B04E30] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nouvelle demande DSAR
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Demandes reçues"
            value={receivedCount}
            icon={<Inbox className="h-5 w-5" />}
          />
          <KPICard
            label="En cours"
            value={inProgressCount}
            icon={<Loader2 className="h-5 w-5" />}
          />
          <KPICard
            label="Terminées"
            value={completedCount}
            icon={<CheckCircle2 className="h-5 w-5" />}
          />
          <KPICard
            label="En retard"
            value={overdueCount}
            icon={<XCircle className="h-5 w-5" />}
          />
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm text-red-800">
              Impossible de charger les demandes DSAR.
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-stone-700">Filtres</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="rounded-md border border-stone-200 px-3 py-1.5 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="rounded-md border border-stone-200 px-3 py-1.5 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {(statusFilter || typeFilter) && (
              <button
                onClick={() => {
                  setStatusFilter("");
                  setTypeFilter("");
                  setPage(1);
                }}
                className="rounded-md px-2.5 py-1.5 text-xs font-medium text-stone-500 hover:bg-stone-100 hover:text-stone-700"
              >
                Reinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Overdue warning */}
        {overdueCount > 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
            <div>
              <p className="text-sm font-medium text-red-800">
                {overdueCount} demande{overdueCount > 1 ? "s" : ""} en retard
              </p>
              <p className="text-xs text-red-600">
                Le delai legal de 30 jours (Loi 2008-12) est depasse pour ces demandes. Action immediate requise.
              </p>
            </div>
          </div>
        )}

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={displayRequests}
          pagination={{
            page: pagination.page,
            limit: pagination.limit,
            total: pagination.total,
          }}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="Aucune demande DSAR trouvée."
        />

        {/* Pagination disclaimer */}
        {pagination && pagination.total > displayRequests.length && (
          <p className="text-xs text-amber-600">
            Compteurs basés sur la page actuelle ({displayRequests.length}/{pagination.total} demandes).
          </p>
        )}

        {/* New DSAR modal */}
        <NewDsarModal
          open={showNewModal}
          onClose={() => setShowNewModal(false)}
          onSubmit={handleNewDsar}
        />
      </div>
    </AdminLayout>
  );
}
